<script lang="ts" setup>
import { onMounted, nextTick, useTemplateRef } from 'vue';
import InfoCard from '@/components/InfoCard.vue';

const wrapperRef = useTemplateRef('treeWrapper');

// 移动端把横滚容器初始定位到画布中心，使根节点（居中）首屏可见
const centerTreeScroll = () => {
  const el = wrapperRef.value;

  if (!el || el.scrollWidth <= el.clientWidth) {
    return;
  }

  el.scrollLeft = (el.scrollWidth - el.clientWidth) / 2;
};

onMounted(() => {
  nextTick(centerTreeScroll);
});
</script>

<template>
  <section class="page-about-organization-structure pt-48px">
    <div class="about-organization-structure-container container <sm:px-16 <xl:px-24">
      <h2 class="m-0 text-center text-32px font-medium <md:text-26">{{ $t('about.organizationStructure.title') }}</h2>

      <div class="tree-scroll-wrapper mt-32px" ref="treeWrapper">
        <div class="tree-inner">
          <div class="logo mt-32px <md:mt-0">
            <img src="@/assets/common/logo-with-title.svg" alt="LOGO" class="mx-auto block w-400px object-contain <md:w-300px">
          </div>

          <InfoCard
            class="before-brand-primary relative mx-auto mt-48px w-720px before:absolute before:left-50% before:top--37px before:ml--1px before:h-38px before:w-px before:content-empty <md:mt-48px <md:w-[calc(100vw-32px)]"
            title-class="min-w-178"
            :title="$t('about.organizationStructure.structure1.title')"
            small
          >
            <p class="m-0 text-16px leading-24px">{{ $t('about.organizationStructure.structure1.content') }}</p>
          </InfoCard>

          <ul class="mx-0 mb-0 mt-48 flex list-none justify-between p-0">
            <li>
              <InfoCard class="h-164px w-442px <2xl:w-352px" title-class="min-w-178" :title="$t('about.organizationStructure.structure3.title')" small>
                <p class="m-0 h-full overflow-y-auto text-16px leading-24px">{{ $t('about.organizationStructure.structure3.content') }}</p>
              </InfoCard>
            </li>
            <li>
              <InfoCard
                class="relative h-164px w-442px before:absolute before:left-50% before:top--48px before:ml--1px before:h-49px before:w-px before:bg-brand-primary before:content-empty <2xl:w-464px"
                title-class="min-w-178"
                :title="$t('about.organizationStructure.structure4.title')"
                small
              >
                <p class="m-0 h-full overflow-y-auto text-16px leading-24px">{{ $t('about.organizationStructure.structure4.content') }}</p>
              </InfoCard>
            </li>
            <li>
              <InfoCard class="h-164px w-442px <2xl:w-352px" title-class="min-w-178" :title="$t('about.organizationStructure.structure2.title')" small>
                <p class="m-0 h-full overflow-y-auto text-16px leading-24px">{{ $t('about.organizationStructure.structure2.content') }}</p>
              </InfoCard>
            </li>
          </ul>

          <ul class="structure-tree relative mx-auto mb-0 mt-48px w-1200px flex list-none justify-between p-0 before:absolute before:right-[calc(50%-0.5px)] before:top--49px before:h-48px before:w-px before:bg-brand-primary before:content-empty <2xl:w-1090px">
            <li class="h-24px w-px"></li>
            <li class="h-24px w-px"></li>
            <li class="h-24px w-px"></li>
            <li class="h-24px w-px"></li>
            <li class="h-24px w-px"></li>
            <li class="h-24px w-px"></li>
            <li class="h-24px w-px"></li>
            <li class="h-24px w-px"></li>
            <li class="h-24px w-px"></li>
          </ul>

          <ul class="structure-tree-text mx-15px mb-0 mt-10px flex list-none justify-between p-0 text-brand-primary <2xl:mx--5px">
            <li class="text-center text-balance text-16px">{{ $t('about.organizationStructure.department1') }}</li>
            <li class="text-center text-balance text-16px">{{ $t('about.organizationStructure.department2') }}</li>
            <li class="text-center text-balance text-16px">{{ $t('about.organizationStructure.department3') }}</li>
            <li class="text-center text-balance text-16px">{{ $t('about.organizationStructure.department4') }}</li>
            <li class="text-center text-balance text-16px">{{ $t('about.organizationStructure.department5') }}</li>
            <li class="text-center text-balance text-16px">{{ $t('about.organizationStructure.department6') }}</li>
            <li class="text-center text-balance text-16px">{{ $t('about.organizationStructure.department7') }}</li>
            <li class="text-center text-balance text-16px">{{ $t('about.organizationStructure.department8') }}</li>
            <li class="text-center text-balance text-16px">{{ $t('about.organizationStructure.department9') }}</li>
          </ul>
        </div>
      </div>
      <p class="tree-scroll-hint m-0 mt-12 text-center text-12 text-brand-gray">{{ $t('about.organizationStructure.scrollHint') }}</p>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.page-about-organization-structure {
  .about-organization-structure-container {
    .before-brand-primary {
      &::before {
        background: linear-gradient(to top, rgba(17, 128, 255, 1) 0%, rgba(17, 128, 255, 0) 100%);
      }
    }

    .structure-tree {
      border-top: 1px solid #1180ff;

      li {
        background: linear-gradient(to bottom, rgba(17, 128, 255, 1) 0%, rgba(17, 128, 255, 0) 100%);
      }
    }

    .structure-tree-text {
      li {
        box-sizing: border-box;
        width: calc(100% / 10);
        padding-right: 10px;
        padding-left: 10px;
      }
    }

    .tree-scroll-wrapper {
      @media (max-width: 1279.9px) {
        padding-right: 10px;
        padding-bottom: 20px;
        padding-left: 10px;
        overflow-x: auto;
      }
    }

    .tree-inner {
      @media (max-width: 1279.9px) {
        min-width: 1200px;
      }
    }

    .tree-scroll-hint {
      display: none;

      @media (max-width: 1279.9px) {
        display: block;
      }
    }
  }
}
</style>
